import React from 'react';
import { Avatar, Box, Button, Flex, IconButton, Link, SimpleGrid, Text } from '@chakra-ui/react';
import { FiChevronLeft, FiCopy, FiEdit3 } from 'react-icons/fi';
import type { Campaign, Client, Collab } from '@foodfluence/shared';
import { ff } from '../../../theme';
import { SUBMISSION_GROUPS, SubmissionGroup } from '../utils/submissionCards';

/** Page body width: phone column per board, wider centred column on large screens. */
export const SUBMISSION_MAX_W = { base: '560px', lg: '720px' };

/** Back button, uppercase eyebrow, condensed title (board 52px × 0.8) and the brand tile. */
export const SubmissionHeader: React.FC<{
  eyebrow: string;
  title: string;
  onBack: () => void;
  client?: Client;
  children?: React.ReactNode;
}> = ({ eyebrow, title, onBack, client, children }) => (
  <>
    <Flex pt="12px" px="12px" align="center" gap="8px">
      <IconButton
        aria-label="Back"
        icon={<FiChevronLeft size={22} strokeWidth={2.4} />}
        onClick={onBack}
        flex="none"
        w="44px"
        h="44px"
        minW="44px"
        borderRadius="full"
        bg="transparent"
        color={ff.ink}
        _hover={{ bg: ff.grey }}
      />
      <Text flex={1} minW={0} noOfLines={1} fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A" lineHeight="normal">
        {eyebrow}
      </Text>
    </Flex>
    <Flex px="20px" pt="10px" align="flex-end" justify="space-between" gap="12px">
      <Flex direction="column" gap="8px" minW={0}>
        <Text as="h1" m={0} textStyle="display" fontSize="36px">
          {title}
        </Text>
        {children}
      </Flex>
      <Avatar
        src={client?.instagramProfilePicture}
        name={client?.accountName}
        boxSize="48px"
        borderRadius="14px"
        bg="#E3E8EF"
        color={ff.ink}
        flex="none"
        sx={{ '& > [role=img]': { fontSize: '15px', fontWeight: 600 }, img: { borderRadius: '14px' } }}
      />
    </Flex>
  </>
);

/** Red "N edits requested" banner. */
export const EditsBanner: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <Flex role="status" p="14px" borderRadius="16px" bg="#FBE1DC" color="#6E1A0A" gap="12px" align="flex-start">
    <Flex flex="none" w="36px" h="36px" borderRadius="full" bg="#A1260F" color="white" align="center" justify="center">
      <FiEdit3 size={18} strokeWidth={2.4} aria-hidden />
    </Flex>
    <Flex direction="column" gap="2px">
      <Text as="strong" fontSize="16px" lineHeight="normal">{title}</Text>
      <Text fontSize="14px" lineHeight={1.4}>{body}</Text>
    </Flex>
  </Flex>
);

/** Shopify affiliate code box: code + copy, discount, minimum order and the brand site. No commission (none exists). */
export const ShopifyCodeBox: React.FC<{
  collab: Collab;
  campaign?: Campaign;
  client?: Client;
  onCopy: (text: string) => void;
}> = ({ collab, campaign, client, onCopy }) => {
  const code = collab.shopifyDiscountCode;
  if (campaign?.campaignType !== 'shopify' || !code) return null;
  const cfg = campaign.shopifyAffiliateDiscountConfig;
  const off = cfg ? (cfg.discountType === 'percentage' ? `${cfg.discountValue}% off` : `$${cfg.discountValue} off`) : null;
  const site = client?.website;
  return (
    <Box as="section" aria-label="Shopify affiliate code" p="14px 14px 14px 16px" borderRadius="18px" border={`1.5px dashed ${ff.ink}`} bg={ff.limeSurface}>
      <Flex align="center" gap="12px">
        <Flex flex={1} minW={0} direction="column" gap="4px">
          <Text fontSize="10px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" color={ff.body} lineHeight="normal">
            Your affiliate code{off ? ` · ${off}` : ''}
          </Text>
          <Text textStyle="display" fontSize="29px" letterSpacing="0.02em" wordBreak="break-all">{code}</Text>
          <Text fontSize="13px" color={ff.body} lineHeight="normal">
            You must include it in your posts
            {cfg && cfg.minOrderAmount > 0 ? ` · followers get ${off} on orders over $${cfg.minOrderAmount}` : ''}
          </Text>
        </Flex>
        <Button
          onClick={() => onCopy(code)}
          aria-label="Copy affiliate code"
          flex="none"
          h="44px"
          px="16px"
          borderRadius="full"
          bg={ff.ink}
          color={ff.lime}
          fontSize="14px"
          fontWeight={600}
          gap="6px"
          _hover={{ bg: ff.ink }}
          _active={{ bg: ff.inkSoft }}
        >
          <FiCopy size={16} aria-hidden />
          Copy
        </Button>
      </Flex>
      {site && (
        <Flex mt="10px" pt="10px" borderTop="1px solid rgba(14,14,14,0.12)" align="center" gap="6px" fontSize="13px" color={ff.body}>
          <Text as="span" flex="none">Direct followers to</Text>
          <Link
            href={site.startsWith('http') ? site : `https://${site}`}
            isExternal
            fontWeight={600}
            color={ff.ink}
            textDecoration="underline"
            noOfLines={1}
            minW={0}
          >
            {site}
          </Link>
          <IconButton
            aria-label="Copy website"
            icon={<FiCopy size={14} />}
            onClick={() => onCopy(site)}
            flex="none"
            w="44px"
            h="44px"
            minW="44px"
            my="-12px"
            borderRadius="full"
            bg="transparent"
            color={ff.ink}
            _hover={{ bg: 'rgba(14,14,14,0.06)' }}
          />
        </Flex>
      )}
    </Box>
  );
};

/** "To do · 2" / "In review · 1" / "Done · 3" sections; cards go two columns from md. */
export function SubmissionGroups<T>({ items, groupOf, render }: {
  items: T[];
  groupOf: (item: T) => SubmissionGroup;
  render: (item: T) => React.ReactNode;
}) {
  return (
    <>
      {SUBMISSION_GROUPS.map(({ key, title }) => {
        const inGroup = items.filter(i => groupOf(i) === key);
        if (inGroup.length === 0) return null;
        return (
          <Flex as="section" key={key} direction="column" gap="10px">
            <Text as="h2" m={0} mt="6px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2} lineHeight="normal">
              {title} · {inGroup.length}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px" alignItems="start">
              {inGroup.map(render)}
            </SimpleGrid>
          </Flex>
        );
      })}
    </>
  );
}

/** Empty state (board grey panel). */
export const SubmissionEmpty: React.FC<{ icon: React.ElementType; title: string; body: string }> = ({ icon: IconEl, title, body }) => (
  <Flex mt="12px" py="32px" px="24px" borderRadius="24px" bg={ff.grey} direction="column" align="center" gap="10px" textAlign="center">
    <Flex w="56px" h="56px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" justify="center">
      <IconEl size={24} aria-hidden />
    </Flex>
    <Text textStyle="display" fontSize="30px" lineHeight={1.12}>{title}</Text>
    <Text maxW="270px" fontSize="14px" lineHeight={1.4} color="#55555A">{body}</Text>
  </Flex>
);
